import { tz } from "@date-fns/tz";
import { useTheme } from "@mui/material";
import { blue } from "@mui/material/colors";
import { addDays, isSameDay } from "date-fns";
import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
  Area,
  Bar,
  ComposedChart,
  Rectangle,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import "components/elements/widgets/EnergyChart/styles.scss";

import { ForecastParameter, GetForecastResponsePrepared, useGetEarnings } from "api";
import { useFiltersContext } from "components/contexts";
import { Widget } from "components/elements/mui";
import { EnergyChartTooltip } from "components/elements/widgets/EnergyChart/components";
import { useFormatMessage } from "lang";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { useChartNavigation } from "utils/hooks/useChartNavigation";

import { ForecastWidgetContent } from "./components";
import { createHelperFunctions, useParamsUtils } from "./utils";

// Energy param shows the predicted energy production for preceiding period (15 minutes / 1 hour)
// that's why we need to substract this period from the date if energy is selected

export const ForecastChart = ({
  data,
}: {
  data: GetForecastResponsePrepared;
}) => {
  const theme = useTheme();
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const { date } = useFiltersContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedParam, setSelectedParam] = useState<ForecastParameter>(
    (searchParams.get("param") as ForecastParameter) || "energy",
  );

  const handleParamChange = (newParam: ForecastParameter) => {
    setSelectedParam(newParam);
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.set("param", newParam);
    setSearchParams(newSearchParams);
  };

  const paramsUtils = useParamsUtils();
  const helperFunctions = useMemo(
    () => createHelperFunctions(selectedParam, selectedPowerPlant),
    [selectedParam, selectedPowerPlant],
  );

  const startDate = useMemo(
    () => helperFunctions.start(date["day"]),
    [helperFunctions, date],
  );
  const endDate = useMemo(
    () => helperFunctions.end(date["day"]),
    [helperFunctions, date],
  );

  // Earnings exist only where prices do: today and tomorrow, and only for
  // plants selling via Tradea. Elsewhere the option is greyed out and a
  // selection that has become impossible falls back to energy.
  const tzName = selectedPowerPlant?.timezone || "Europe/Warsaw";
  const timezone = useMemo(() => tz(tzName), [tzName]);
  const now = new Date();
  const withinPricedDays =
    isSameDay(date["day"], now, { in: timezone }) ||
    isSameDay(date["day"], addDays(now, 1), { in: timezone });
  const { data: earnings } = useGetEarnings(date["day"], withinPricedDays);
  const earningsDay = earnings?.configured ? earnings : null;
  const earningsAvailable =
    withinPricedDays && (earnings === undefined || earnings.configured);
  useEffect(() => {
    if (selectedParam === "earnings" && !earningsAvailable) handleParamChange("energy");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [earningsAvailable, selectedParam]);

  const preparedData = useMemo(() => {
    if (!data) return undefined;
    const prepared = helperFunctions.prepareData(data, date["day"]);
    if (selectedParam !== "earnings") return prepared;
    // Keyed by the quarter's UTC instant: unlike "HH:mm" it stays unique on
    // the 25-hour October day.
    const byQuarter = new Map(
      (earningsDay?.quarters ?? []).map((q) => [q.start, q.expectedOursPln ?? undefined]),
    );
    return {
      ...prepared,
      minutely15: prepared.minutely15.map((r) => ({
        ...r,
        earnings: byQuarter.get(r.date.toISOString()),
      })),
    };
  }, [data, helperFunctions, date, selectedParam, earningsDay]);
  const earningsPln = earningsDay
    ? (earningsDay.projectedOursPln ?? earningsDay.totals.expectedOursPln)
    : null;

  const seriesType = paramsUtils[selectedParam].chartOptions.seriesType as
    | "line"
    | "bar";

  const {
    lockedIndex,
    chartEventHandlers,
    tooltipProps,
    navigationProps,
    renderIndicators,
  } = useChartNavigation({
    data: preparedData?.minutely15,
    dataKey: selectedParam,
    seriesType,
    seriesColor: blue[500],
    theme,
    getX: (r) => r.date.getTime(),
  });

  const noData = useMemo(() => !data || data.minutely15.length === 0, [data]);
  return (
    <>
      <Widget
        title={formatMessage(paramsUtils[selectedParam].title)}
        size={{
          xs: 12,
        }}
        content={
          <ForecastWidgetContent
            selectedParam={selectedParam}
            setSelectedParam={handleParamChange}
            currentDaily={preparedData?.currentDaily}
            currentHourly={preparedData?.currentMinutely15}
            earningsPln={earningsPln}
            earningsAvailable={earningsAvailable}
          />
        }
      />
      <Widget
        size={{ xs: 12 }}
        content={
          <ResponsiveContainer height={300} style={{ position: "relative" }}>
            <ComposedChart
              data={preparedData?.minutely15}
              margin={{
                top: 20,
                right: 10,
                bottom: 0,
                left: noData ? -50 : 0,
              }}
              {...chartEventHandlers}
            >
              {noData || !preparedData?.minutely15 ? (
                <text
                  x="50%"
                  y="50%"
                  textAnchor="middle"
                  fill={theme.palette.text.disabled}
                >
                  {formatMessage("chart.noData")}
                </text>
              ) : (
                <>
                  <XAxis
                    dataKey="date"
                    scale={paramsUtils[selectedParam].chartOptions.scaleType}
                    ticks={helperFunctions.generateTicks(date["day"])}
                    tickFormatter={(value) =>
                      helperFunctions.formatXAxis(value)
                    }
                    min={startDate.getTime()}
                    max={endDate.getTime()}
                    tick={{
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                      fontFamily: theme.typography.fontFamily,
                    }}
                    axisLine={{
                      stroke: theme.palette.divider,
                      strokeWidth: 1,
                    }}
                  />
                  <YAxis
                    dataKey={selectedParam}
                    tickFormatter={(value) =>
                      helperFunctions.formatYAxis(value)
                    }
                    tick={{
                      fill: theme.palette.text.primary,
                      fontSize: 12,
                      fontFamily: theme.typography.fontFamily,
                    }}
                    axisLine={{
                      stroke: theme.palette.divider,
                      strokeWidth: 1,
                    }}
                  />
                  <Tooltip
                    content={
                      <EnergyChartTooltip
                        formatLabel={helperFunctions.formatXTooltip}
                        formatValue={helperFunctions.formatYTooltip}
                        minWidth={170}
                        {...navigationProps}
                      />
                    }
                    position={{ y: 0 }}
                    {...tooltipProps}
                  />
                  {renderIndicators()}
                  <Bar
                    dataKey={selectedParam}
                    fill={theme.palette.secondary.main}
                    activeBar={<Rectangle fill={"#413ea0"} />}
                    activeIndex={lockedIndex !== null ? lockedIndex : undefined}
                    isAnimationActive={false}
                    hide={seriesType !== "bar"}
                  />
                  <Area
                    type="monotone"
                    baseValue="dataMin"
                    dataKey={selectedParam}
                    stroke={blue[500]}
                    strokeWidth={2}
                    fill="url(#forecastColorGradient)"
                    activeDot={lockedIndex !== null ? false : undefined}
                    isAnimationActive={false}
                    hide={seriesType !== "line"}
                  />
                  <defs>
                    <linearGradient
                      id="forecastColorGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="5%"
                        stopColor={blue[500]}
                        stopOpacity={0.9}
                      />
                      <stop
                        offset="70%"
                        stopColor={blue[500]}
                        stopOpacity={0.3}
                      />
                      <stop
                        offset="95%"
                        stopColor={blue[500]}
                        stopOpacity={0}
                      />
                    </linearGradient>
                  </defs>
                </>
              )}
            </ComposedChart>
          </ResponsiveContainer>
        }
      />
    </>
  );
};
